import type { PropsWithChildren } from "react";
import { useCallback } from "react";
import type { Release, ReleaseAdapterState } from "@packages/commons";

import { releaseService } from "@/app/providers/services";
import { useFullRelease, useUpdateFullRelease } from "@/common/hooks_useFullRelease";
import { useNotify } from "@/common/notifications";
import { extractRelease } from "@/features/adapters/releaseResponse";
import { AdapterWorkflowProvider } from "./AdapterWorkflowContext";

export const ConnectedAdapterWorkflowProvider = ({ children }: PropsWithChildren) => {
  const release = useFullRelease() ?? undefined;
  const updateRelease = useUpdateFullRelease();
  const notify = useNotify();
  const releaseMutation = releaseService.useMutateResource(release, {});
  const writeAdapters = useCallback(
    async (adapters: ReleaseAdapterState[], current: Release) => {
      try {
        return extractRelease(await releaseMutation.action({ adapters }), current);
      } finally {
        updateRelease();
      }
    },
    [releaseMutation, updateRelease],
  );
  const onSaveError = useCallback(
    (error: unknown) =>
      notify.error(error instanceof Error ? error.message : "Could not save the Adapter changes.", 8000),
    [notify],
  );

  return (
    <AdapterWorkflowProvider
      release={release}
      writeAdapters={writeAdapters}
      onSaveError={onSaveError}
    >
      {children}
    </AdapterWorkflowProvider>
  );
};
